import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Room, RoomEvent, Track } from 'livekit-client';
import { ApiService } from '../../api.service';
import { EchoService } from '../../services/echo.service';
import { environment } from '../../../environments/environment';

interface ChatMsg { name: string; text: string; time: Date; mine: boolean; }

@Component({
  selector: 'app-live-rooms',
  templateUrl: './live-rooms.component.html',
  styleUrls: ['./live-rooms.component.css']
})
export class LiveRoomsComponent implements OnInit, OnDestroy {
  @ViewChild('stage', { static: true }) stageRef!: ElementRef<HTMLDivElement>;
  @ViewChild('frame', { static: true }) frameRef!: ElementRef<HTMLDivElement>;
  @ViewChild('chatBox') chatBoxRef?: ElementRef<HTMLDivElement>;

  readonly fallbackAvatar = 'assets/images/avatar.png';
  myId = Number(localStorage.getItem('user_id'));
  myName = '';

  // rooms list
  streams: any[] = [];
  loadingList = true;
  listError = '';
  tab: 'live' | 'mine' = 'live';
  search = '';

  // jo room dekh rahe hain
  selected: any = null;
  connecting = false;
  ended = false;
  error = '';
  hasVideo = false;
  audioBlocked = false;
  muted = false;
  viewers = 0;
  people: { name: string; guest: boolean }[] = [];   // host ke ilawa baqi log
  hostOnline = false;

  // right panel
  side: 'chat' | 'people' | 'info' = 'chat';
  chat: ChatMsg[] = [];
  newMsg = '';
  chatError = '';
  copied = false;

  room?: Room;                 // template mein bhi use hota hai, is liye public
  private joinSeq = 0;
  private timer: any;

  constructor(
    private http: HttpClient,
    private echo: EchoService,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    if (this.myId) {
      this.apiService.getUserById(this.myId).subscribe(
        (r: any) => this.myName = [r?.Users?.firstname, r?.Users?.lastname].filter(Boolean).join(' '),
        () => {}
      );
    }
    this.loadStreams();
    this.timer = setInterval(() => this.loadStreams(), 15000);
  }

  /* ---------------- rooms list ---------------- */
  loadStreams() {
    this.http.get<any>(`${environment.apiUrl}live/active`, { headers: this.echo.headers() }).subscribe({
      next: (r) => { this.streams = r?.streams || []; this.loadingList = false; this.listError = ''; },
      error: () => { this.loadingList = false; this.listError = 'Could not load live rooms.'; }
    });
  }

  get filtered(): any[] {
    const q = this.search.trim().toLowerCase();
    return this.streams.filter(s => {
      if (this.tab === 'mine' && Number(s.user?.id) !== this.myId) { return false; }
      return !q || this.fullName(s.user).toLowerCase().includes(q) || this.sub(s.user).toLowerCase().includes(q);
    });
  }

  trackByToken(_i: number, s: any) { return s.token; }

  /* ---------------- watch a room ---------------- */
  get ownRoom(): boolean {
    return !!this.selected && Number(this.selected.user?.id) === this.myId;
  }

  get canChat(): boolean {
    return !!this.room && !!this.selected && !this.ownRoom && !this.connecting && !this.ended && !this.error;
  }

  get stageMsg(): string {
    if (!this.selected) { return 'Select a live room to start watching'; }
    if (this.ownRoom) { return 'This is your own live room. You are broadcasting from the Go Live page.'; }
    if (this.connecting) { return 'Connecting…'; }
    if (this.error) { return this.error; }
    if (this.ended) { return 'This live has ended.'; }
    if (!this.hasVideo) { return 'Waiting for the host video…'; }
    return '';
  }

  async watch(s: any) {
    if (this.selected?.token === s.token) { return; }
    this.leave();
    this.selected = s;
    if (this.ownRoom) { return; }

    const seq = ++this.joinSeq;
    this.connecting = true;
    try {
      // logged-in member token: naam + chat ke saath (public viewer-token mein chat band hai)
      const res: any = await firstValueFrom(
        this.http.post(`${environment.apiUrl}live/${s.token}/member-token`, {}, { headers: this.echo.headers() })
      );
      if (seq !== this.joinSeq) { return; }

      const room = new Room();
      this.room = room;
      const stage = this.stageRef.nativeElement;

      room.on(RoomEvent.TrackSubscribed, (track: Track) => { if (this.room === room) { this.attach(track); } });
      room.on(RoomEvent.TrackUnsubscribed, (track: Track) => {
        track.detach().forEach(el => el.remove());
        this.hasVideo = stage.querySelector('video') !== null;
      });
      room.on(RoomEvent.ParticipantConnected, () => {
        this.syncPeople();
        if (this.hostOnline) { this.ended = false; }
      });
      room.on(RoomEvent.ParticipantDisconnected, () => {
        this.syncPeople();
        if (!this.hostOnline) { this.ended = true; }   // host chala gaya
      });
      room.on(RoomEvent.AudioPlaybackStatusChanged, () => (this.audioBlocked = !room.canPlaybackAudio));
      room.on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: any, _kind?: any, topic?: string) =>
        this.onData(payload, participant, topic));

      await room.connect(res.server_url, res.token);
      if (seq !== this.joinSeq) { room.disconnect(); return; }

      this.connecting = false;
      this.syncPeople();
      room.remoteParticipants.forEach(p =>
        p.trackPublications.forEach(pub => { if (pub.track) { this.attach(pub.track); } })
      );
      this.ended = !this.hostOnline;
    } catch (e: any) {
      if (seq !== this.joinSeq) { return; }
      this.connecting = false;
      this.error = e?.error?.message || 'This live room is not available or has ended.';
    }
  }

  leave() {
    this.joinSeq++;
    this.room?.disconnect();
    this.room = undefined;
    this.stageRef.nativeElement.innerHTML = '';
    this.selected = null;
    this.connecting = false;
    this.ended = false;
    this.error = '';
    this.hasVideo = false;
    this.audioBlocked = false;
    this.viewers = 0;
    this.people = [];
    this.hostOnline = false;
    this.chat = [];
    this.chatError = '';
    this.side = 'chat';
  }

  private attach(track: Track) {
    if (track.kind !== Track.Kind.Video && track.kind !== Track.Kind.Audio) { return; }
    if (document.getElementById(`lr-track-${track.sid}`)) { return; }
    const el = track.attach();
    el.id = `lr-track-${track.sid}`;
    el.muted = this.muted;
    this.stageRef.nativeElement.appendChild(el);
    if (track.kind === Track.Kind.Video) { this.hasVideo = true; this.ended = false; }
  }

  // identity formats (LiveKitController / LiveViewerController se):
  //   host & members -> "<user_id>_<random>"      public guest -> "viewer_<random>"
  private syncPeople() {

    const room = this.room;
    if (!room) { return; }
    const hostId = String(this.selected?.user?.id);
    const all = Array.from(room.remoteParticipants.values()).map(p => ({
      name: p.name || '',
      host: p.identity.split('_')[0] === hostId || p.identity === `rtmp_${hostId}`,
      guest: p.identity.startsWith('viewer_')
    }));
    this.hostOnline = all.some(p => p.host);
    this.people = all.filter(p => !p.host).map(p => ({ name: p.name, guest: p.guest }));
    this.viewers = this.people.length + 1;   // +1 = main khud
  }

  get totalPeople(): number {
    return this.room ? this.people.length + 1 + (this.hostOnline ? 1 : 0) : 0;
  }

  /* ---------------- stage controls ---------------- */
  enableSound() { this.room?.startAudio(); this.audioBlocked = false; }

  toggleSound() {
    this.muted = !this.muted;
    this.stageRef.nativeElement.querySelectorAll('audio,video')
      .forEach(el => ((el as HTMLMediaElement).muted = this.muted));
  }

  fullscreen() {
    if (document.fullscreenElement) { document.exitFullscreen(); return; }
    this.frameRef.nativeElement.requestFullscreen?.();
  }

  /* ---------------- chat (LiveKit data channel) ---------------- */
  sendChat() {
    const text = this.newMsg.trim();
    if (!text || !this.room || !this.canChat) { return; }
    const name = this.myName || 'Viewer';
    this.chatError = '';

    this.room.localParticipant
      .publishData(new TextEncoder().encode(JSON.stringify({ name, text })), { reliable: true, topic: 'chat' })
      .then(() => this.pushChat({ name, text, time: new Date(), mine: true }))
      .catch(() => (this.chatError = 'Chat is not available in this room.'));
    this.newMsg = '';
  }

  private onData(payload: Uint8Array, participant?: any, topic?: string) {
    if (topic && topic !== 'chat') { return; }
    try {
      const m = JSON.parse(new TextDecoder().decode(payload));
      if (!m || typeof m.text !== 'string') { return; }
      this.pushChat({
        name: String(m.name || participant?.name || 'Viewer').slice(0, 60),
        text: m.text.slice(0, 500),
        time: new Date(),
        mine: false
      });
    } catch { /* ghalat packet ignore */ }
  }

  private pushChat(m: ChatMsg) {
    this.chat = [...this.chat, m].slice(-200);
    setTimeout(() => {
      const el = this.chatBoxRef?.nativeElement;
      if (el) { el.scrollTop = el.scrollHeight; }
    }, 30);
  }

  /* ---------------- info ---------------- */
  get link(): string {
    return this.selected ? `${window.location.origin}/live/${this.selected.token}` : '';
  }

  copyLink() {
    if (!this.link) { return; }
    navigator.clipboard?.writeText(this.link).then(() => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    });
  }

  /* ---------------- helpers ---------------- */
  fullName(u: any): string {
    return [u?.firstname, u?.lastname].filter(Boolean).join(' ') || 'Host';
  }

  sub(u: any): string {
    return [u?.job_title, u?.company].filter(Boolean).join(' | ');
  }

  photo(u: any): string {
    const p = u?.profile_image;
    if (!p || typeof p !== 'string') { return this.fallbackAvatar; }
    if (p.startsWith('data:image') || p.startsWith('http')) { return p; }
    if (p.length < 300 && p.includes('/')) { return p; }
    return 'data:image/jpeg;base64,' + p;
  }

  onImgError(e: any) {
    const img = e.target as HTMLImageElement;
    if (img.dataset['fb']) { return; }
    img.dataset['fb'] = '1';
    img.src = this.fallbackAvatar;
  }

  liveFor(s: any): string {
    const t = new Date(s?.started_at).getTime();
    if (isNaN(t)) { return 'Live now'; }
    const m = Math.max(0, Math.floor((Date.now() - t) / 60000));
    if (m < 1) { return 'Just started'; }
    if (m < 60) { return `Live for ${m} min`; }
    return `Live for ${Math.floor(m / 60)} h ${m % 60} min`;
  }

  ngOnDestroy() {
    clearInterval(this.timer);
    this.leave();
  }
}
